// src/pages/QualifiedChoreographies.js - choreographies qualified for the Grand Finále.
//
// Audit fixes: FL-12 (eligibility from the finale's allowedCategories, "already
// registered" state from finaleRegistration, after registering go straight to the new
// entry with a toast), FL-02 (backend reason shown), FL-19 (labels), FL-29 (dialog).
// Seasons (2.1): tabs per season (components/leader/SeasonTabs.js), newest first. A
// qualification is only for the Grand Finále of its own season (the server refuses others).
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarDays, CheckCircle2, ChevronRight, Music2, Trophy } from 'lucide-react';
import useFetch from '../hooks/useFetch';
import { choreographiesApi, competitionsApi, getErrorMessage } from '../services/api';
import { ALL_VALID_COMBINATIONS, categoryLabel } from '../constants/categories';
import { formatDate, formatDateLong, isRegistrationClosed } from '../utils/format';
import { Alert, Button, ConfirmDialog, EmptyState, PageHeader, Spinner, useToast } from '../components/ui';
import LoadError from '../components/leader/LoadError';
import SeasonTabs, { useSeasonTabs } from '../components/leader/SeasonTabs';

const LEGACY_LEAGUES = { 'ZÁKLADNÍ': 'BASIC', PROFI: 'PROFESSIONAL' };
const normLeague = (league) => LEGACY_LEAGUES[league] || league;
const yearOf = (iso) => Number(String(iso || '').slice(0, 4)) || null;
const seasonOf = (choreography) => choreography.season || yearOf(choreography.competition?.date);
const isFinale = (c) => c.isGrandFinale || c.type === 'grand_finale';

/** Can `choreography` enter `finale`? Same rules as the backend: the category the results gave
 *  for the finale (finaleLeague / finaleDiscipline) or the registered one, against the allowed list. */
export function isEligible(choreography, finale) {
  const league = normLeague(choreography.finaleLeague || choreography.league);
  const discipline = choreography.finaleDiscipline || choreography.discipline;
  const matches = (c) =>
    c.discipline === discipline && c.ageCategory === choreography.ageCategory && normLeague(c.league) === league;
  const allowed = Array.isArray(finale.allowedCategories) && finale.allowedCategories.length > 0 ? finale.allowedCategories : null;
  if (allowed) return allowed.some(matches);
  return ALL_VALID_COMBINATIONS.some(matches);
}

function QualifiedCard({ choreography, finales, isPast, onRegister, busyId }) {
  const registration = choreography.finaleRegistration;
  const season = seasonOf(choreography);
  const moved =
    (choreography.finaleLeague && normLeague(choreography.finaleLeague) !== normLeague(choreography.league)) ||
    (choreography.finaleDiscipline && choreography.finaleDiscipline !== choreography.discipline);
  return (
    <article className="rounded-lg bg-white p-4 shadow sm:p-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <Trophy className="h-5 w-5 flex-shrink-0 text-amber-500" aria-hidden="true" />
            {choreography.name}
          </h2>
          <p className="mt-0.5 text-sm text-gray-600">
            {categoryLabel(choreography.discipline, choreography.ageCategory, choreography.league)}
          </p>
          {moved && (
            <p className="mt-0.5 text-sm font-medium text-indigo-700">
              Na Grand Finále v kategorii{' '}
              {categoryLabel(
                choreography.finaleDiscipline || choreography.discipline,
                choreography.ageCategory,
                choreography.finaleLeague || choreography.league
              )}
            </p>
          )}
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="h-4 w-4" aria-hidden="true" />
              Kvalifikace: {choreography.competition?.name} ({formatDate(choreography.competition?.date)})
            </span>
            <span className="inline-flex items-center gap-1">
              <Music2 className="h-4 w-4" aria-hidden="true" />
              {choreography.musicUploaded ? 'Hudba nahraná' : 'Chybí hudba'}
            </span>
          </p>
        </div>
        <Link
          to={`/choreographies/${choreography.id}`}
          className="text-sm font-medium text-indigo-700 hover:text-indigo-900"
        >
          Detail choreografie
        </Link>
      </div>

      <div className="mt-4 border-t border-gray-100 pt-4">
        {registration ? (
          <div className="flex flex-col gap-2 rounded-md bg-green-50 p-3 text-sm text-green-900 sm:flex-row sm:items-center sm:justify-between">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              Přihlášeno na {registration.competitionName}
            </span>
            <Link
              to={`/choreographies/${registration.id}`}
              className="inline-flex items-center gap-1 font-medium text-indigo-700 hover:text-indigo-900"
            >
              Otevřít přihlášku
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        ) : isPast ? (
          <p className="text-sm text-gray-600">Kvalifikace platila pro Grand Finále {season}, na Grand Finále se nepřihlásila.</p>
        ) : finales.length === 0 ? (
          <p className="text-sm text-gray-600">Registrace na Grand Finále {season} zatím není otevřená.</p>
        ) : (
          <ul className="space-y-3">
            {finales.map((finale) => {
              const eligible = isEligible(choreography, finale);
              return (
                <li
                  key={finale.id}
                  className="flex flex-col gap-3 rounded-md border border-gray-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <p className="font-medium text-gray-900">{finale.name}</p>
                    <p className="text-sm text-gray-600">{formatDateLong(finale.date)}</p>
                    {!eligible && (
                      <p className="mt-1 text-sm text-gray-700">
                        Tato kategorie není na tomto Grand Finále vypsaná, choreografii sem nelze přihlásit.
                      </p>
                    )}
                  </div>
                  {eligible && (
                    <Button
                      onClick={() => onRegister(choreography, finale)}
                      loading={busyId === choreography.id}
                      className="w-full sm:w-auto"
                    >
                      <Trophy className="h-4 w-4" aria-hidden="true" />
                      Přihlásit na Grand Finále
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </article>
  );
}

const QualifiedChoreographies = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { data, error, isLoading, reload } = useFetch(async () => {
    const [qualifiedRes, competitionsRes] = await Promise.all([
      choreographiesApi.getQualified(),
      competitionsApi.getAll(),
    ]);
    return { qualified: qualifiedRes.data || [], competitions: competitionsRes.data || [] };
  }, 'all');

  const [pending, setPending] = useState(null); // { choreography, finale }
  const [busyId, setBusyId] = useState(null);
  const [dialogError, setDialogError] = useState(null);

  const qualified = useMemo(() => data?.qualified || [], [data]);
  const { tabs, selected: season, select: selectSeason, currentSeason } = useSeasonTabs(qualified, seasonOf);

  // Only the Grand Finále of the shown season (a qualification is for its own season).
  const finales = useMemo(
    () =>
      (data?.competitions || []).filter(
        (c) => isFinale(c) && c.status === 'active' && !isRegistrationClosed(c) && yearOf(c.date) === season
      ),
    [data, season]
  );
  const upcomingFinales = useMemo(
    () => (data?.competitions || []).filter((c) => isFinale(c) && c.status === 'upcoming' && yearOf(c.date) === season),
    [data, season]
  );

  const closeDialog = useCallback(() => {
    setPending(null);
    setDialogError(null);
  }, []);

  const handleRegister = async () => {
    if (!pending) return;
    const { choreography, finale } = pending;
    setBusyId(choreography.id);
    setDialogError(null);
    try {
      const response = await choreographiesApi.registerForFinale(choreography.id, finale.id);
      toast.success('Přihlášeno na Grand Finále. Teď nahrajte video pozadí a potvrďte prezenci.');
      navigate(`/choreographies/${response.data.id}`);
    } catch (err) {
      setDialogError(getErrorMessage(err));
      setBusyId(null);
    }
  };

  if (isLoading && !data) return <Spinner label="Načítám kvalifikované choreografie…" />;
  if (error && !data) {
    return <LoadError title="Kvalifikované choreografie se nepodařilo načíst" error={error} onRetry={reload} />;
  }

  const shown = qualified.filter((c) => seasonOf(c) === season);
  const isPast = Boolean(currentSeason && season && season < currentSeason);
  const openForRegistration = shown.some((c) => !c.finaleRegistration);
  const register = (choreography, finale) => {
    setDialogError(null);
    setPending({ choreography, finale });
  };

  return (
    <div>
      <PageHeader
        title="Grand Finále"
        subtitle="Choreografie, které se kvalifikovaly na Grand Finále. Přihlášením vznikne nová přihláška, ve které nahrajete video pozadí a potvrdíte prezenci."
      />

      {qualified.length === 0 ? (
        <EmptyState icon={Trophy} title="Zatím nemáte žádné kvalifikované choreografie">
          Kvalifikaci na Grand Finále udělují organizátoři po semifinálových soutěžích.
        </EmptyState>
      ) : (
        <div className="space-y-6">
          <SeasonTabs tabs={tabs} selected={season} onSelect={selectSeason} />

          {!isPast && finales.length === 0 && openForRegistration && (
            <Alert tone="info">
              Registrace na Grand Finále {season} teď není otevřená.
              {upcomingFinales.length > 0 &&
                ` ${upcomingFinales
                  .map((f) => `${f.name}${f.registrationStart ? `: registrace od ${formatDate(f.registrationStart)}` : ''}`)
                  .join(', ')}.`}
            </Alert>
          )}

          {shown.length === 0 ? (
            <EmptyState icon={Trophy} title={`V sezóně ${season} zatím nemáte kvalifikované choreografie`}>
              Kvalifikaci na Grand Finále udělují organizátoři po semifinálových soutěžích. Starší sezóny najdete v
              záložkách výše.
            </EmptyState>
          ) : (
            <div className="space-y-4">
              {shown.map((c) => (
                <QualifiedCard
                  key={c.id}
                  choreography={c}
                  finales={finales}
                  isPast={isPast}
                  busyId={busyId}
                  onRegister={register}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {pending && (
        <ConfirmDialog
          title="Přihlásit na Grand Finále?"
          confirmLabel="Přihlásit"
          loading={busyId !== null}
          error={dialogError}
          onConfirm={handleRegister}
          onCancel={closeDialog}
        >
          <p>
            Choreografie <strong>{pending.choreography.name}</strong> bude přihlášena na{' '}
            <strong>{pending.finale.name}</strong> ({formatDate(pending.finale.date)}).
          </p>
          <p>
            Každou choreografii lze přihlásit jen na jedno Grand Finále. Přihlášku do Grand Finále pak najdete v
            Choreografiích.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
};

export default QualifiedChoreographies;
